/**
 * /app/knowledge-library — Agency Expert Knowledge / Prompt Library
 * (Task 071).
 *
 * Operational management UI for the agency-curated prompt blocks the
 * AI content-calendar workers retrieve at run time. Visible only to
 * leadership (owner / marketing_manager) with
 * `ai.knowledge_pack.manage`. Backend RLS is the load-bearing gate;
 * the FE permission check just hides the route from non-leadership
 * users so they don't see a permission-denied page.
 *
 * What this page does NOT do:
 *   - It does NOT generate content.
 *   - It does NOT show the AI runs that consumed any block.
 *   - It does NOT delete blocks. Archive is the only retirement
 *     path.
 *   - It does NOT show client-portal users anything; the sidebar
 *     entry is hidden for them upstream.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { isApiError, KmiApiError } from '../api/client';
import {
  createAiPromptBlock,
  listAiPromptBlocks,
  listClients,
  transitionAiPromptBlockStatus,
  updateAiPromptBlock,
} from '../api/endpoints';
import type {
  AiPromptBlockRow,
  AiPromptBlockScope,
  AiPromptBlockStatus,
  ClientRow,
  ContentVariantPlatform,
  CreateAiPromptBlockInput,
  UpdateAiPromptBlockInput,
} from '../api/types';
import { useMeContext } from '../context/MeContextProvider';
import { hasAnyPermission, PERM } from '../permissions';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import {
  PROMPT_BLOCK_TEMPLATES,
  type PromptBlockTemplate,
} from './agencyKnowledgeLibraryTemplates';

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly blocks: ReadonlyArray<AiPromptBlockRow> }
  | { readonly status: 'forbidden' }
  | { readonly status: 'error'; readonly error: unknown };

type EditorMode =
  | { readonly kind: 'closed' }
  | { readonly kind: 'create' }
  | { readonly kind: 'edit'; readonly block: AiPromptBlockRow };

interface FilterState {
  readonly status: AiPromptBlockStatus | 'all';
  readonly scope: AiPromptBlockScope | 'all';
  readonly clientId: string | 'all';
}

const ALL_PLATFORMS: ReadonlyArray<ContentVariantPlatform> = [
  'instagram_feed',
  'instagram_story',
  'instagram_reel',
  'facebook_post',
  'facebook_story',
  'tiktok_video',
  'snapchat_story',
];

const ALL_SCOPES: ReadonlyArray<AiPromptBlockScope> = [
  'agency',
  'client',
  'industry',
  'platform',
  'service',
  'task',
];

const ALL_STATUSES: ReadonlyArray<AiPromptBlockStatus> = ['draft', 'approved', 'archived'];

export function AgencyKnowledgeLibraryPage(): JSX.Element {
  const { me } = useMeContext();
  const canManage =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.AI_KNOWLEDGE_PACK_MANAGE);

  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [clients, setClients] = useState<ReadonlyArray<ClientRow>>([]);
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    scope: 'all',
    clientId: 'all',
  });
  const [editor, setEditor] = useState<EditorMode>({ kind: 'closed' });
  const [reloadKey, setReloadKey] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!canManage) {
      setState({ status: 'forbidden' });
      return;
    }
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    setActionError(null);
    Promise.all([
      listAiPromptBlocks({}, { signal: ctrl.signal }),
      listClients({ signal: ctrl.signal }),
    ])
      .then(([blocksResp, clientsResp]) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', blocks: blocksResp.ai_prompt_blocks });
        setClients(clientsResp.clients);
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [canManage, reloadKey]);

  const visibleBlocks = useMemo(() => {
    if (state.status !== 'ready') return [];
    return state.blocks.filter((b) => {
      if (filters.status !== 'all' && b.status !== filters.status) return false;
      if (filters.scope !== 'all' && b.scope !== filters.scope) return false;
      if (filters.clientId !== 'all') {
        if (filters.clientId === '__none__') {
          if (b.client_id !== null) return false;
        } else if (b.client_id !== filters.clientId) {
          return false;
        }
      }
      return true;
    });
  }, [state, filters]);

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients) map.set(c.id, c.name);
    return map;
  }, [clients]);

  if (!canManage) {
    return (
      <main className="page" dir="rtl">
        <header className="page-header">
          <h1>مكتبة المعرفة</h1>
        </header>
        <EmptyState
          title="لا تملك صلاحية إدارة مكتبة المعرفة"
          description="تواصل مع مالك الوكالة أو مدير التسويق لمنحك صلاحية ai.knowledge_pack.manage."
        />
      </main>
    );
  }

  async function handleTransition(block: AiPromptBlockRow, target: AiPromptBlockStatus) {
    try {
      setActionError(null);
      await transitionAiPromptBlockStatus({ id: block.id, target_status: target });
      setReloadKey((n) => n + 1);
    } catch (err) {
      setActionError(extractMessage(err));
    }
  }

  return (
    <main className="page" dir="rtl">
      <header className="page-header">
        <div>
          <h1>مكتبة المعرفة</h1>
          <p className="meta">
            كتل توجيه وأمثلة يستخدمها مولّد المحتوى الذكي عند توليد التقاويم. الكتل المعتمدة فقط
            تُستخدم في التوليد. مسوّدة → اعتماد → أرشفة.
          </p>
        </div>
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            setActionError(null);
            setEditor({ kind: 'create' });
          }}
        >
          كتلة جديدة
        </button>
      </header>

      <section className="filter-bar" aria-label="فلترة">
        <label className="lbl">
          الحالة
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({ ...f, status: e.target.value as FilterState['status'] }))
            }
          >
            <option value="all">الكل</option>
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="lbl">
          النطاق
          <select
            value={filters.scope}
            onChange={(e) =>
              setFilters((f) => ({ ...f, scope: e.target.value as FilterState['scope'] }))
            }
          >
            <option value="all">الكل</option>
            {ALL_SCOPES.map((s) => (
              <option key={s} value={s}>
                {scopeLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="lbl">
          العميل
          <select
            value={filters.clientId}
            onChange={(e) => setFilters((f) => ({ ...f, clientId: e.target.value }))}
          >
            <option value="all">الكل</option>
            <option value="__none__">بدون عميل (نطاق وكالة/منصة/قطاع/خدمة/مهمّة)</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      {actionError ? (
        <div className="card error" style={{ padding: 12 }}>
          {actionError}
        </div>
      ) : null}

      {state.status === 'loading' ? <LoadingState title="جاري تحميل الكتل…" /> : null}
      {state.status === 'error' ? (
        <ErrorState
          title="تعذّر تحميل المكتبة"
          error={state.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {state.status === 'ready' && visibleBlocks.length === 0 ? (
        <EmptyState
          title="لا توجد كتل مطابقة"
          description="ابدأ بإنشاء كتلة توجيه جديدة، أو غيّر الفلاتر."
        />
      ) : null}
      {state.status === 'ready' && visibleBlocks.length > 0 ? (
        <ul className="row-list">
          {visibleBlocks.map((block) => (
            <li key={block.id} className="row-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <strong>{block.title}</strong>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span className={`pill pill-${statusVariant(block.status)}`}>
                    {statusLabel(block.status)}
                  </span>
                  <span className="pill pill-info">{scopeLabel(block.scope)}</span>
                  {block.client_id ? (
                    <span className="pill pill-warning">
                      للعميل فقط:{' '}
                      {clientNameById.get(block.client_id) ?? block.client_id.slice(0, 8)}
                    </span>
                  ) : null}
                </div>
              </div>
              <p
                className="body"
                style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {block.body}
              </p>
              <div className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
                v{block.version} · أولوية {block.priority}
                {block.industry ? ` · قطاع: ${block.industry}` : ''}
                {block.service_key ? ` · خدمة: ${block.service_key}` : ''}
                {block.platforms.length > 0
                  ? ` · منصات: ${block.platforms.map(platformLabel).join('، ')}`
                  : ''}
                {block.applies_to.length > 0 ? ` · قدرات: ${block.applies_to.join('، ')}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {block.status === 'draft' ? (
                  <>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => {
                        setActionError(null);
                        setEditor({ kind: 'edit', block });
                      }}
                    >
                      تعديل
                    </button>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => handleTransition(block, 'approved')}
                    >
                      اعتماد
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleTransition(block, 'archived')}
                    >
                      أرشفة
                    </button>
                  </>
                ) : null}
                {block.status === 'approved' ? (
                  <>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleTransition(block, 'draft')}
                    >
                      إعادة فتح للتعديل
                    </button>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleTransition(block, 'archived')}
                    >
                      أرشفة
                    </button>
                  </>
                ) : null}
                {block.status === 'archived' ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={() => handleTransition(block, 'draft')}
                  >
                    استرجاع كمسوّدة
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {editor.kind !== 'closed' ? (
        <BlockEditorModal
          mode={editor}
          clients={clients}
          onClose={() => setEditor({ kind: 'closed' })}
          onSaved={() => {
            setEditor({ kind: 'closed' });
            setReloadKey((n) => n + 1);
          }}
        />
      ) : null}
    </main>
  );
}

interface BlockEditorModalProps {
  readonly mode: EditorMode;
  readonly clients: ReadonlyArray<ClientRow>;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

function BlockEditorModal(props: BlockEditorModalProps): JSX.Element | null {
  // Capture once so the narrowing survives across the closure used
  // by the submit handler.
  const mode = props.mode;
  const initial = mode.kind === 'edit' ? mode.block : null;

  const [scope, setScope] = useState<AiPromptBlockScope>(initial?.scope ?? 'agency');
  const [clientId, setClientId] = useState<string | null>(initial?.client_id ?? null);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [industry, setIndustry] = useState(initial?.industry ?? '');
  const [serviceKey, setServiceKey] = useState(initial?.service_key ?? '');
  const [priority, setPriority] = useState(initial?.priority ?? 100);
  const [platforms, setPlatforms] = useState<ReadonlyArray<ContentVariantPlatform>>(
    initial?.platforms ?? [],
  );
  const [appliesTo, setAppliesTo] = useState<string>(initial?.applies_to.join(', ') ?? '');
  const [examplesText, setExamplesText] = useState<string>(
    initial && initial.examples.length > 0 ? JSON.stringify(initial.examples, null, 2) : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (mode.kind === 'closed') return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    let examples: ReadonlyArray<unknown> = [];
    const trimmedExamples = examplesText.trim();
    if (trimmedExamples.length > 0) {
      try {
        const parsed = JSON.parse(trimmedExamples);
        if (!Array.isArray(parsed)) {
          throw new Error('examples must be a JSON array');
        }
        examples = parsed;
      } catch {
        setError('أمثلة JSON غير صالحة. يجب أن تكون مصفوفة JSON.');
        return;
      }
    }

    const appliesToList = appliesTo
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const cleanIndustry = industry.trim();
    const cleanServiceKey = serviceKey.trim();
    const effectiveClientId = scope === 'client' ? clientId : null;

    setSubmitting(true);
    try {
      if (mode.kind === 'create') {
        const input: CreateAiPromptBlockInput = {
          scope,
          client_id: effectiveClientId,
          title: title.trim(),
          body: body.trim(),
          examples,
          applies_to: appliesToList,
          platforms,
          industry: cleanIndustry.length > 0 ? cleanIndustry : null,
          service_key: cleanServiceKey.length > 0 ? cleanServiceKey : null,
          priority,
        };
        await createAiPromptBlock(input);
      } else {
        const input: UpdateAiPromptBlockInput = {
          id: (mode as Extract<EditorMode, { kind: 'edit' }>).block.id,
          scope,
          client_id: effectiveClientId,
          title: title.trim(),
          body: body.trim(),
          examples,
          applies_to: appliesToList,
          platforms,
          industry: cleanIndustry.length > 0 ? cleanIndustry : null,
          service_key: cleanServiceKey.length > 0 ? cleanServiceKey : null,
          priority,
        };
        await updateAiPromptBlock(input);
      }
      props.onSaved();
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Task 077 — starter-template picker. Only shown on create. Selecting
  // a template overwrites the visible form fields; nothing reaches the
  // API until the operator clicks the existing "إنشاء كمسوّدة" button.
  function applyTemplate(t: PromptBlockTemplate): void {
    setScope(t.fill.scope);
    if (t.fill.scope !== 'client') setClientId(null);
    setTitle(t.fill.title);
    setBody(t.fill.body);
    setIndustry(t.fill.industry ?? '');
    setServiceKey(t.fill.service_key ?? '');
    setPriority(t.fill.priority);
    setPlatforms(t.fill.platforms);
    setAppliesTo(t.fill.applies_to.join(', '));
    setExamplesText('');
    setError(null);
  }

  return (
    <div className="modal-overlay" onClick={props.onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} dir="rtl">
        <header className="modal-header">
          <h2>{mode.kind === 'create' ? 'كتلة جديدة' : 'تعديل كتلة'}</h2>
          <button type="button" className="btn-icon" onClick={props.onClose}>
            ×
          </button>
        </header>
        <form onSubmit={handleSubmit} className="modal-body">
          {mode.kind === 'create' ? (
            <label className="lbl">
              استخدام قالب (اختياري)
              <select
                defaultValue=""
                onChange={(e) => {
                  const t = PROMPT_BLOCK_TEMPLATES.find((x) => x.id === e.target.value);
                  // Reset the select so the same template can be
                  // re-applied after manual edits without forcing a
                  // page refresh.
                  e.target.value = '';
                  if (t) applyTemplate(t);
                }}
              >
                <option value="">— ابدأ من قالب جاهز —</option>
                {PROMPT_BLOCK_TEMPLATES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              <span className="tiny" style={{ color: 'var(--muted)' }}>
                يملأ الحقول فقط. لا يحفظ شيئاً حتى تضغط زر الإنشاء.
              </span>
            </label>
          ) : null}
          <label className="lbl">
            النطاق
            <select
              value={scope}
              onChange={(e) => {
                const next = e.target.value as AiPromptBlockScope;
                setScope(next);
                if (next !== 'client') setClientId(null);
              }}
            >
              {ALL_SCOPES.map((s) => (
                <option key={s} value={s}>
                  {scopeLabel(s)}
                </option>
              ))}
            </select>
          </label>
          {scope === 'client' ? (
            <label className="lbl">
              العميل (للعميل فقط)
              <select
                value={clientId ?? ''}
                onChange={(e) => setClientId(e.target.value || null)}
                required
              >
                <option value="">— اختر —</option>
                {props.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span className="tiny" style={{ color: 'var(--warning)' }}>
                ⚠ هذه الكتلة تظهر للذكاء الاصطناعي عند توليد محتوى لهذا العميل فقط.
              </span>
            </label>
          ) : null}
          <label className="lbl">
            العنوان
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              required
            />
          </label>
          <label className="lbl">
            النصّ
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              maxLength={8000}
              required
            />
          </label>
          {scope === 'industry' ? (
            <label className="lbl">
              القطاع (مطلوب لنطاق قطاع)
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                maxLength={200}
                required
              />
            </label>
          ) : null}
          {scope === 'service' ? (
            <label className="lbl">
              مفتاح الخدمة (مطلوب لنطاق خدمة)
              <input
                type="text"
                value={serviceKey}
                onChange={(e) => setServiceKey(e.target.value)}
                maxLength={80}
                required
              />
            </label>
          ) : null}
          {scope === 'platform' ? (
            <fieldset className="lbl">
              <legend>المنصات (واحدة على الأقل)</legend>
              {ALL_PLATFORMS.map((p) => (
                <label key={p} style={{ display: 'inline-flex', gap: 6, marginInlineEnd: 12 }}>
                  <input
                    type="checkbox"
                    checked={platforms.includes(p)}
                    onChange={(e) => {
                      setPlatforms((cur) =>
                        e.target.checked ? [...cur, p] : cur.filter((x) => x !== p),
                      );
                    }}
                  />
                  {platformLabel(p)}
                </label>
              ))}
            </fieldset>
          ) : null}
          <label className="lbl">
            القدرات (applies_to) — مفصولة بفواصل
            <input
              type="text"
              value={appliesTo}
              onChange={(e) => setAppliesTo(e.target.value)}
              placeholder="content.calendar_generate.unified, content.calendar_generate.platform_adapted"
            />
          </label>
          <label className="lbl">
            أمثلة (مصفوفة JSON اختيارية)
            <textarea
              value={examplesText}
              onChange={(e) => setExamplesText(e.target.value)}
              rows={4}
              placeholder='[{"good":"...","bad":"..."}]'
            />
          </label>
          <label className="lbl">
            الأولوية (0–1000)
            <input
              type="number"
              min={0}
              max={1000}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) || 0)}
            />
          </label>
          {error ? <p className="error">{error}</p> : null}
          <footer className="modal-footer">
            <button type="button" className="btn" onClick={props.onClose} disabled={submitting}>
              إلغاء
            </button>
            <button type="submit" className="btn primary" disabled={submitting}>
              {submitting
                ? 'جاري الحفظ…'
                : mode.kind === 'create'
                  ? 'إنشاء كمسوّدة'
                  : 'حفظ المسوّدة'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function statusLabel(s: AiPromptBlockStatus): string {
  switch (s) {
    case 'draft':
      return 'مسوّدة';
    case 'approved':
      return 'معتمدة';
    case 'archived':
      return 'مؤرشفة';
    default:
      return s;
  }
}

function statusVariant(s: AiPromptBlockStatus): string {
  switch (s) {
    case 'draft':
      return 'draft';
    case 'approved':
      return 'active';
    case 'archived':
      return 'archived';
    default:
      return 'info';
  }
}

function scopeLabel(s: AiPromptBlockScope): string {
  switch (s) {
    case 'agency':
      return 'وكالة';
    case 'client':
      return 'عميل';
    case 'industry':
      return 'قطاع';
    case 'platform':
      return 'منصة';
    case 'service':
      return 'خدمة';
    case 'task':
      return 'مهمّة';
    default:
      return s;
  }
}

function platformLabel(p: ContentVariantPlatform): string {
  switch (p) {
    case 'instagram_feed':
      return 'Instagram · منشور';
    case 'instagram_story':
      return 'Instagram · ستوري';
    case 'instagram_reel':
      return 'Instagram · ريل';
    case 'facebook_post':
      return 'Facebook · منشور';
    case 'facebook_story':
      return 'Facebook · ستوري';
    case 'tiktok_video':
      return 'TikTok · فيديو';
    case 'snapchat_story':
      return 'Snapchat · ستوري';
    default:
      return p;
  }
}

function extractMessage(err: unknown): string {
  if (err instanceof KmiApiError) return err.message ?? 'فشل غير معروف';
  if (isApiError(err)) return err.message ?? 'فشل غير معروف';
  if (err instanceof Error) return err.message;
  return 'فشل غير معروف';
}
